import React, { useCallback, useEffect } from "react";
import {
  PortraitImage,
  LandscapeImage,
  VerticalImage,
  TikTokVideo,
  PortraitVideo,
  LandscapeVideo,
  VerticalVideo,
  RawVideo,
  WaveImage,
  DIMENSIONS_BY_ORIENTATION,
  OneToneImage,
  DefaultImage
} from "@talentbait/remotion-templates-talentbait";

import { useTemplateDimensions } from "./useTemplateDimensions";
import { MediaAssetsLabStore, PreviewStore, withRefluxStore } from "../../../../../Stores";
import { ActionsMediaAssetsLab } from "../../../../../Actions/ActionsMediaAssetsLab/ActionsMediaAssetsLab";

import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { Group } from "../../../../../Components/Layout";
import { getImageDimensions, isVideo } from "../../utils";
import { removeCloudinaryParams, updateCloudinaryUrlParams } from "../layout/Layout";
import { VideoPreview } from "./template-item/VideoPreview";
import { RawVideoPreview } from "./template-item/RawVideoPreview";
import { ImageOrientationPreview } from "./template-item/ImageOrientationPreview";

const IMAGE_TEMPLATES = { PortraitImage, LandscapeImage, VerticalImage };
const VIDEO_TEMPLATES = { TikTokVideo, PortraitVideo, LandscapeVideo, VerticalVideo };

export const IMAGE_MULTI_TEMPLATES = { WaveImage, OneToneImage, DefaultImage };
const VIDEO_MULTI_TEMPLATES = { RawVideo };

export const TEMPLATES_ORIENTATIONS = Object.keys(DIMENSIONS_BY_ORIENTATION);

export const allTemplates = {
  ...IMAGE_TEMPLATES,
  ...VIDEO_TEMPLATES,
  ...IMAGE_MULTI_TEMPLATES,
  ...VIDEO_MULTI_TEMPLATES
};

export const multiTemplates = {
  ...IMAGE_MULTI_TEMPLATES,
  ...VIDEO_MULTI_TEMPLATES
};

const PreviewArea = ({
  // Store
  previewInputProps,
  selectedTemplates,
  templateInputProps,
  previewSelectedAsset
}) => {
  const dimensions = useTemplateDimensions();

  useEffect(() => {
    Object.keys(allTemplates).forEach((templateKey) => {
      if (Object.keys(multiTemplates).includes(templateKey)) {
        TEMPLATES_ORIENTATIONS.map((orientation) => {
          const templateKeyFormatted = `${templateKey}_${orientation}`;
          ActionsMediaAssetsLab.constructTemplateInputProps(templateKeyFormatted);
        });
      } else {
        ActionsMediaAssetsLab.constructTemplateInputProps(templateKey);
      }
    });
  }, [previewInputProps.backgroundImage, previewInputProps.imageUrl, previewInputProps.video]);

  const handleTemplateClick = useCallback(
    async (ev) => {
      ev.stopPropagation();

      const target = ev.target.closest("[data-action]");
      if (!target) return;

      const { action, templatekey, templateorientation, storekey, storevalue, ismultiorientation } =
        target.dataset;

      if (action === "select") {
        const isTemplateSelected = selectedTemplates.some(
          (template) => template.templateKey === templatekey
        );

        if (!isTemplateSelected) {
          const isMultiOrientation = Boolean(ismultiorientation === "true");

          const newTemplates = [
            ...selectedTemplates,
            {
              templateKey: templatekey,
              templateOrientation: templateorientation,
              isMultiOrientation
            }
          ];

          ActionsMediaAssetsLab.selectTemplate(newTemplates);
        } else {
          const newTemplates = selectedTemplates.filter(
            (template) => template.templateKey !== templatekey
          );
          ActionsMediaAssetsLab.selectTemplate(newTemplates);
        }
      } else if (action === "autocrop") {
        let templateId = templatekey;
        let orientation = null;
        let dimensionTemplateKey = dimensions[templatekey] || dimensions.default;

        const isCropped = templateInputProps[templateId]?.isCropped;

        //TODO CHANGE RawVideo to multi orientation not priority now
        if (Object.keys(multiTemplates).some((key) => templatekey.includes(key))) {
          const splitTemplateName = templatekey.split("_");
          templateId = splitTemplateName[0];
          orientation = splitTemplateName[1];
          dimensionTemplateKey = dimensions[templateId][orientation] || dimensions.default;
        }

        const assetDimensions = await getImageDimensions(storevalue);
        const { width, cloudAr, height } = dimensionTemplateKey;

        let editedImage;

        if (isCropped) {
          editedImage = removeCloudinaryParams(storevalue);
        } else {
          editedImage = updateCloudinaryUrlParams({
            url: storevalue,
            w: assetDimensions.width || width,
            h: assetDimensions.height || height,
            ar: cloudAr
          });
        }

        ActionsMediaAssetsLab.changeTemplateInputProps({
          templateKey: templatekey,
          key: storekey,
          value: editedImage
        });

        ActionsMediaAssetsLab.changeTemplateInputProps({
          templateKey: templatekey,
          key: "isCropped",
          value: !isCropped
        });
      }
    },
    [selectedTemplates, templateInputProps]
  );

  return (
    <Group direction="column" gap="25px" align="unset" style={{ paddingBottom: "50px" }}>
      <ResponsiveMasonry columnsCountBreakPoints={{ 1000: 2, 1200: 4, 3000: 6 }}>
        {isVideo(previewSelectedAsset) ? (
          <Masonry gutter="5px">
            {TEMPLATES_ORIENTATIONS.map((orientation) => {
              const templateKey = `RawVideo_${orientation}`;

              return (
                <RawVideoPreview
                  key={templateKey}
                  orientation={orientation}
                  templateKey={templateKey}
                  dimensions={dimensions}
                  templateInputProps={templateInputProps}
                  selectedTemplates={selectedTemplates}
                  handleTemplateClick={handleTemplateClick}
                  previewInputProps={previewInputProps}
                />
              );
            })}
          </Masonry>
        ) : (
          <Masonry gutter="5px">
            {/* Testing new multi orientation functionality */}
            {Object.entries(IMAGE_MULTI_TEMPLATES).flatMap(([templateKey, TemplateComponent]) =>
              TEMPLATES_ORIENTATIONS.map((orientation) => {
                const combinedTemplateKey = `${templateKey}_${orientation}`;
                const newPreviewInputProps = { ...previewInputProps, orientation };

                return (
                  <ImageOrientationPreview
                    key={combinedTemplateKey}
                    orientation={orientation}
                    templateKey={combinedTemplateKey}
                    TemplateComponent={TemplateComponent}
                    dimensions={dimensions}
                    templateInputProps={templateInputProps}
                    selectedTemplates={selectedTemplates}
                    handleTemplateClick={handleTemplateClick}
                    previewInputProps={newPreviewInputProps}
                  />
                );
              })
            )}

            {Object.entries(VIDEO_TEMPLATES).map(([templateKey, TemplateComponent]) => {
              return (
                <VideoPreview
                  key={templateKey}
                  templateKey={templateKey}
                  TemplateComponent={TemplateComponent}
                  dimensions={dimensions}
                  templateInputProps={templateInputProps}
                  selectedTemplates={selectedTemplates}
                  handleTemplateClick={handleTemplateClick}
                  previewInputProps={previewInputProps}
                />
              );
            })}
          </Masonry>
        )}
      </ResponsiveMasonry>
    </Group>
  );
};

export default withRefluxStore(
  PreviewArea,
  [MediaAssetsLabStore, PreviewStore],
  ["previewInputProps", "selectedTemplates", "templateInputProps", "previewSelectedAsset"]
);
